import { Divider, Grid, Paper } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Alert, Form, SimpleTable, Table } from "../../layout";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input, InputCheck, Select } from "../../controls";
import Label from "./Label";
import { AppContext } from "../../App";
import { DropLi, Model, UseForm } from "../../hooks";
import { formatDate, PrintDiv, ToFormData } from "../../hooks/Method";
import InputSwitch from "../../controls/InputSwitch";
import { action, controller, Server } from "../../api/Server";
import ThermalPrinterRecept from "./printReceipt/ThermalPrinterReceipt";
import GeneralPrinterReceipt from "./printReceipt/GeneralPrinterReceipt";

const ReturnForm = () => {
  const { id } = useParams();
  const { org, settings, selStore, setPageTitle, authUser } =
    useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState();
  const { ReturnProdMdl } = Model();
  const { ReturnReason } = DropLi();
  const [reasons, setReasons] = useState([]);
  const { values, setValues } = UseForm(ReturnProdMdl);
  const [loading, setLoading] = useState(false);
  const [receiptData, setReceiptData] = useState({});
  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    setPageTitle(t("sellReturnForm"));
    if (id && selStore && org && org.id && authUser && authUser.id) {
      setLoading(true);
      Server(controller.Sell + action.GetById).getById(id, (res) => {
        setLoading(false);
        if (res.status === 200) {
          setInvoice(res.result);
          setValues((j) => ({
            ...j,
            sellId: res.result.id,
            storeId: selStore,
            orgId: org.id,
            userId: authUser.id,
            refundDate: new Date().toISOString(),
            items: res.result.sellItems.map((j, i) => ({
              ...j,
              key: i + 1,
              id: "",
              isSellable: true,
              sellItemId: j.id,
              returnQty: 0,
              returnReason: 1,
              subtotal: 0,
              userId: authUser.id,
            })),
          }));
        }
      });
    }

    setReasons([...ReturnReason.filter((j) => j.id !== 6)]);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selStore, org, authUser]);

  useEffect(() => {
    if (invoice && values) {
      // Calculate refund amount.
      var _refundAmt = values.items
        .map((item) => parseFloat(item.returnQty) * item.price)
        .reduce((x, y) => x + y, 0);

      // Calculate discount amount.
      var _disAmt = 0;
      if (invoice.discountType === 1)
        _disAmt = _refundAmt * (invoice.discount / 100);
      else _disAmt = invoice.discount;

      // Calculate tax amount.
      var _taxAmt = _refundAmt * (invoice.taxPct / 100);

      setValues((j) => ({
        ...j,
        refundAmt: _refundAmt.toFixed(settings.decimal),
        cutDiscount: _disAmt,
        refundTaxAmt: _taxAmt,
        payableRefund: (_refundAmt - _disAmt + _taxAmt).toFixed(
          settings.decimal
        ),
      }));
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.items]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    Server(controller.SellReturn + action.Post).post(
      ToFormData({
        ...values,
        items: values.items
          .filter((j) => parseFloat(j.returnQty) > 0)
          .map((j) => ({ ...j, product: "" })),
        refundDate: new Date().toISOString(),
      }),
      (res) => {
        setReceiptData({ ...res.result });
        setTimeout(() => {
          setLoading(false);
          PrintDiv("printSellReturnRecept");
          navigate("/pos");
        }, 1500);
      }
    );
  };

  const handleInpChg = (param) => {
    setValues((val) => ({
      ...val,
      items: val.items.map((j) =>
        j.key === param.key ? { ...j, [param.name]: param.value } : j
      ),
    }));
  };

  const handleInpCheck = (e) => {
    setValues((j) => ({ ...j, [e.target.name]: e.target.checked }));
  };

  return invoice && org && values.sellId ? (
    <>
      <div className="d-none" id="printSellReturnRecept">
        {receiptData.id &&
          (settings && settings.store && settings.store.receiptType === 2 ? (
            <GeneralPrinterReceipt data={receiptData} />
          ) : (
            <ThermalPrinterRecept data={receiptData} />
          ))}
      </div>
      <Form noValidate onSubmit={handleSubmit}>
        <div className="d-grid g-20">
          {/* ====== Details ====== */}
          <Paper>
            <div className="p-10 d-flex j-between a-center">
              <div>
                <span className="fs-18 tx-upp gray-color fw-5 border-left">
                  {t("sellReturnForm")}
                </span>
              </div>
              <div>
                <Button
                  type="submit"
                  loading={loading}
                  sx={{ minWidth: "100px" }}
                >
                  {t("submit")}
                </Button>
              </div>
            </div>
            <Divider />
            <Grid container spacing={3} className="p-15">
              <Label name={t("invoiceID")} value={invoice.id} />
              <Label name={t("date")} value={formatDate(invoice.sellDate)} />
              <Label
                name={t("businessLocation")}
                value={
                  invoice.store &&
                  invoice.store.storeName + " | " + invoice.store.address
                }
              />

              <Label
                name={t("name")}
                value={
                  invoice.cstmr ? invoice.cstmr.fullName : "Walk-In Customer"
                }
              />
              {invoice.cstmr && (
                <>
                  <Label name={t("customerId")} value={invoice.cstmrId} />
                  <Label
                    name={t("phone")}
                    value={invoice.cstmr && invoice.cstmr.phone}
                  />
                </>
              )}
            </Grid>

            <Divider />
            {/* ===== Accounts ===== */}
            <Grid container>
              <Grid item xs={12} md={6}>
                <SimpleTable
                  list={[
                    {
                      label: t("subtotal"),
                      value: `${settings.curSymbol} ${invoice.subtotal.toFixed(
                        settings.decimal
                      )}`,
                    },
                    {
                      label: `${t("tax")} (${invoice.taxPct}%)`,
                      value: `(+) ${settings.curSymbol} ${invoice.tax.toFixed(
                        settings.decimal
                      )}`,
                    },
                    {
                      label: `${t("discount")} ${
                        invoice.discountType === 1 && `(${invoice.discount}%)`
                      }`,
                      value: `(-) ${
                        settings.curSymbol
                      } ${invoice.disAmt.toFixed(settings.decimal)}`,
                    },
                    {
                      disable: !(invoice.dueAmt > 0),
                      label: t("due"),
                      value: (
                        <span>
                          {settings.curSymbol}{" "}
                          {invoice.dueAmt.toFixed(settings.decimal)}
                        </span>
                      ),
                    },
                    {
                      disable: !(invoice.creditAmt > 0),
                      label: t("usedCredit"),
                      value: `${settings.curSymbol} ${invoice.creditAmt.toFixed(
                        settings.decimal
                      )}`,
                    },
                    {
                      label: t("totalPay"),
                      value: `${settings.curSymbol} ${invoice.totalPay.toFixed(
                        settings.decimal
                      )}`,
                    },
                    {
                      label: t("paymentReceived"),
                      value: `${settings.curSymbol} ${invoice.received.toFixed(
                        settings.decimal
                      )}`,
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SimpleTable
                  list={[
                    {
                      label: t("refund"),
                      value: (
                        <span>
                          {settings.curSymbol} {values.refundAmt}
                        </span>
                      ),
                    },
                    {
                      label: t("minusDiscountAmount"),
                      value: (
                        <span>
                          {"(-) "}
                          {settings.curSymbol}{" "}
                          {values.cutDiscount.toFixed(settings.decimal)}
                        </span>
                      ),
                    },
                    {
                      label: t("refundTaxAmt"),
                      value: (
                        <span>
                          {"(+) "}
                          {settings.curSymbol}{" "}
                          {values.refundTaxAmt.toFixed(settings.decimal)}
                        </span>
                      ),
                    },
                    {
                      label: (
                        <span className="error-color">
                          {t("payableRefund")}
                        </span>
                      ),
                      value: (
                        <span className="error-color">
                          {settings.curSymbol} {values.payableRefund}
                        </span>
                      ),
                    },
                  ]}
                />
                {invoice.cstmr && (
                  <div className="ml-20">
                    <InputCheck
                      label={
                        <span className="fs-16 fw-5 tx-upp">
                          {t("keepRefundAsCredit")}
                        </span>
                      }
                      className="gray-color"
                      onChange={handleInpCheck}
                      name="keepRefundAsCredit"
                      checked={values.keepRefundAsCredit}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
          </Paper>
          {/* ====== Product List ======  */}
          <>
            <Table
              ActionComp={() => <></>}
              HeaderComp={() => <></>}
              showHeader={false}
              showSearch={false}
              tblHead={[
                { id: "serial", label: "#" },
                { id: "prod", label: t("productDetails") },
                { id: "sellable", label: t("isSellable") + " ?" },
                { id: "returnQty", label: t("returnQuantity") },
                { id: "reason", label: t("returnReason") },
                { id: "subtotal", label: t("returnSubtotal") },
              ]}
              tblData={values.items.map((item, ind) => {
                return {
                  ...item,
                  serial: ind + 1,
                  sellable: (
                    <>
                      <InputSwitch
                        size="small"
                        className="gray-color"
                        onChange={(e) =>
                          handleInpChg({
                            name: "isSellable",
                            key: item.key,
                            value: e.target.checked,
                          })
                        }
                        checked={item.isSellable}
                        name="isSellable"
                      />
                    </>
                  ),
                  prod: (
                    <div className="d-grid">
                      <span className="fs-14">{item.product.label}</span>
                      <div className="fs-12 d-flex g-5 fw-5">
                        <span>
                          {t("price")}:&nbsp;
                          {settings.curSymbol +
                            " " +
                            item.price.toFixed(settings.decimal)}
                        </span>{" "}
                        |{" "}
                        <span>
                          {t("qty")}:&nbsp;
                          {item.qty.toFixed(settings.decimal)}
                        </span>
                      </div>
                    </div>
                  ),

                  returnQty: (
                    <>
                      <Input
                        placeholder={t("returnQuantity")}
                        name="returnQty"
                        type="number"
                        size="small"
                        variant="outlined"
                        value={item.returnQty}
                        onInput={(e) =>
                          e.target.value > 0 ? e : (e.target.value = "")
                        }
                        onChange={(e) =>
                          (parseFloat(e.target.value) <= item.qty ||
                            item.returnQty.length > e.target.value.length) &&
                          handleInpChg({
                            key: item.key,
                            name: "returnQty",
                            value: e.target.value,
                          })
                        }
                        InputProps={{
                          onFocus: (event) => event.target.select(),
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            padding: "0px 0px",
                            maxWidth: "150px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "5px 10px",
                          },
                        }}
                      />
                    </>
                  ),
                  reason: (
                    <Select
                      variant="outlined"
                      placeholder={t("selectReason")}
                      name="returnReason"
                      size="small"
                      fullWidth
                      value={item.returnReason}
                      onChange={(e) =>
                        handleInpChg({
                          key: item.key,
                          name: "returnReason",
                          value: e.target.value,
                        })
                      }
                      options={reasons}
                      sx={{
                        "& .MuiInputBase-root": {
                          padding: "0px 10px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "5.5px 0px",
                          textAlign: "center",
                        },
                      }}
                    />
                  ),
                  subtotal: `${settings.curSymbol} ${(
                    parseFloat(item.returnQty) * item.price
                  ).toFixed(settings.decimal)}`,
                };
              })}
            />
          </>
        </div>
      </Form>
      {/* Alert  */}
      <Alert alert={alert} setAlert={setAlert} />
    </>
  ) : (
    "Loading..."
  );
};

export default ReturnForm;
